import ServiceCard from './ServiceCard';

export default function ServicesSection() {
  const services = [
    {
      icon: 'ri-file-shield-2-line',
      title: 'Consultancy & Compliance',
      description: 'Helping organisations identify risk, meet regulatory obligations and demonstrate due diligence through practical assessments, planning and governance support.',
      features: ['Risk & Vulnerability Assessments', 'Martyn\'s Law / Protect Duty Readiness', 'Gap Analysis & Roadmaps', 'Policies & Documentation', 'Incident Planning', 'Safeguarding', 'ISO-Aligned Governance']
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Preparedness',
      description: 'Equipping teams with the skills and knowledge to respond effectively to emergencies, reduce workplace risk and meet training obligations.',
      features: ['First Aid & Emergency Response', 'Safety & Compliance', 'Wellbeing & Vulnerability', 'Security & Response', 'Mental Health First Aid', 'Fire Safety Training', 'Accredited Online Learning']
    },
    {
      icon: 'ri-tools-line',
      title: 'Technical & System Solutions',
      description: 'Coordinating the installation and integration of security and safety systems through accredited partners to protect people, property and operations.',
      features: ['CCTV Installation', 'Fire Alarm Systems', 'Access Control', 'Electrical Testing (EICR)', 'Solar PV & Battery Storage', 'EV Charging', 'Cable Laying']
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Security & Event Operations',
      description: 'Providing professional security personnel for events, close protection and operational support where additional resource or specialist capability is required.',
      features: ['Close Protection Services', 'Corporate Event Security', 'Private Event Security', 'Backfill & Additional Guarding', 'Site Security Support', 'Event Risk Management']
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white to-gray-50" aria-label="JL Security Services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Service Divisions</h6>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 font-poppins">
            End-to-End <strong>Risk &amp; Compliance</strong> Support
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
            From assessment and planning through to training, technical coordination and ongoing support — one partner for your complete <strong>safety and compliance</strong> journey across <strong>North Wales</strong> and the UK
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>

        {/* How We Work With You Section */}
        <article className="mt-20 bg-white rounded-2xl p-10 shadow-lg border border-gray-100">
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">How We Work With You</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Assessment</h4>
              <p className="text-sm text-gray-600 font-inter leading-relaxed">
                Identify risk, evaluate current controls and establish what needs to be in place to meet your obligations and protect your operations.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Implementation</h4>
              <p className="text-sm text-gray-600 font-inter leading-relaxed">
                Deliver training, coordinate technical installations and put the necessary systems, policies and procedures in place through accredited partners.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Support</h4>
              <p className="text-sm text-gray-600 font-inter leading-relaxed">
                Provide ongoing maintenance, documentation updates and compliance support to ensure you remain prepared and inspection-ready.
              </p>
            </div>
          </div>
        </article>

        {/* Who We Support Section */}
        <article className="mt-16 bg-white rounded-2xl p-10 shadow-lg border border-gray-100">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center font-poppins">Who We Support</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-hotel-line', text: 'Holiday Parks & Tourism' },
              { icon: 'ri-restaurant-line', text: 'Venues & Hospitality' },
              { icon: 'ri-plant-line', text: 'Farms & Rural Properties' },
              { icon: 'ri-shopping-bag-line', text: 'Retail & Leisure' },
              { icon: 'ri-building-line', text: 'Small Business & Industrial' },
              { icon: 'ri-school-line', text: 'Schools & Education' },
              { icon: 'ri-community-line', text: 'Public Buildings' },
              { icon: 'ri-hammer-line', text: 'Construction Projects' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-2xl text-red-600`}></i>
                </div>
                <p className="text-sm text-gray-700 font-inter">{item.text}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Specialist Packages Section */}
        <article className="mt-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 border border-gray-100">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center font-poppins">Specialist Packages</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">
                    <a href="/martyns-law" className="text-gray-900 hover:text-red-600 transition-colors">Martyn&apos;s Law / Protect Duty Readiness</a>
                  </h4>
                  <p className="text-sm text-gray-600 font-inter">Helping organisations prepare for new protective security requirements through gap analysis, risk assessment and practical action planning.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-hotel-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Holiday Park & Tourism Safety Packages</h4>
                  <p className="text-sm text-gray-600 font-inter">Integrated fire safety, first aid, safeguarding and emergency response planning tailored to seasonal and public-facing tourism operations.</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Why Choose Us Section */}
        <div className="mt-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-10 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center font-poppins">Why Organisations Choose <strong>JL Security</strong></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'One partner instead of multiple contractors',
              'Real operational and compliance experience',
              'Training aligned to identified risks',
              'Technical work by accredited specialists',
              'Practical and proportionate approach',
              'North Wales based with UK capability',
              'Online learning — national reach'
            ].map((reason, index) => (
              <div key={index} className="flex items-start">
                <i className="ri-check-double-line text-2xl mr-3 mt-1 flex-shrink-0"></i>
                <p className="text-sm font-inter leading-relaxed">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
